import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map, timeout } from 'rxjs/operators';
import { GlobalSearchService } from './globalsearchservice.service';
import { RuntimeConfigLoaderService } from 'runtime-config-loader';

export interface Tpl3plConfig {
	client_id?: string;
	integration_type: 'API' | 'PGS' | 'EDI' | 'SFTP' | 'FTP';
	enabled: boolean;
	test_mode: boolean;
	warehouse_code: string;
	config_json: {
		api?: {
			baseUrl?: string;
			apiKey?: string;
			apiSecret?: string;
			timeout?: number;
			retryAttempts?: number;
		};
		pgs?: {
			baseUrl?: string;
			/** Full URL for POST token; if omitted, {baseUrl}/api/Security/Token (CustomerIntegrator). */
			tokenUrl?: string;
			userId?: string;
			passwd?: string;
			defaultWarehouse?: string;
			defaultOutboundChannel?: 'B2B' | 'B2C';
			b2cServiceType?: string;
			/** CurrentInventory maxResults per request (1–2000). Server paginates until a short page. */
			inventoryMaxResults?: number;
			/** ERP loccode used as default target for PGS inventory when mapping is incomplete (must exist in locations). */
			inventoryLoccode?: string;
			warehouseMap?: { [key: string]: string };
			b2bB2cMapping?: { ordertype?: { [key: string]: string }; salestype?: { [key: string]: string } };
			/** PGS SKU line-of-business is sent as CompanyID on POST/PUT /api/Sku (per PGS360 API doc). */
			lob?: {
				default?: string;
				/** ERP manufacturer_id (from manufacturer table) → exact PGS CompanyID string. Takes precedence over byManufacturerName. */
				byManufacturerId?: { [key: string]: string };
				/** Keys match substrings of ERP manufacturer.name (case-insensitive); values are exact strings for PGS. */
				byManufacturerName?: { [key: string]: string };
			};
		};
		edi?: {
			tradingPartnerId?: string;
			isaQualifier?: string;
			gsQualifier?: string;
		};
		sftp?: {
			host?: string;
			port?: number;
			username?: string;
			password?: string;
			remotePath?: string;
			outboundPath?: string;
		};
		mapping?: {
			orderStatus?: { [key: string]: string };
			carrierMapping?: { [key: string]: string };
			b2bB2c?: { ordertype?: { [key: string]: string }; salestype?: { [key: string]: string } };
		};
		allowed_statuses?: number[];
		blocked_statuses?: number[];
		shipped_statuses?: number[];
		auto_invoice_on_shipment?: boolean;
		email_customer_on_3pl_invoice?: boolean;
		status_on_export_success?: number | string | null;
		status_on_shipment_received?: number | string | null;
		status_on_tracking_in_transit?: number | string | null;
		status_on_tracking_delivered?: number | string | null;
		status_on_invoice_failed?: number | string | null;
		status_on_shipment_approved?: number | string | null;
		sku_categories?: string[];
		sku_lines?: string[];
		sku_manufacturers?: number[];
		/** When true, 3PL inventory pull still writes stockmoves but skips GL posting. */
		skip_gl_for_3pl_inventory?: boolean;
		/** Move short 3PL lines to a new back-order SO (preview/execute + optional auto before export). */
		split_partial_backorder_enabled?: boolean;
		/** Allow bump SO # for resubmit independently of split backorder feature. Set directly on the client's 3PL config row. */
		bump_so_resubmit_enabled?: boolean;
		/** Move uninvoiced open qty to a new SO; invoiced lines stay (queue / API). */
		split_uninvoiced_open_lines_enabled?: boolean;
		auto_split_partial_before_export?: boolean;
		/** When true, export holds not_fillable and partial fillability orders (ERP walk); use auto-split or manual split so only fillable lines go to the 3PL. */
		skip_not_fillable_orders?: boolean;
		/** Hold window (minutes) for not_fillable orders before re-evaluating. Default 60. */
		not_fillable_hold_minutes?: number;
		backorder_orderstatusid?: number | string | null;
		backorder_orderstatus_name?: string;
		queue_backorder_for_tpl3pl?: boolean;
		/** Order status ID to assign when split back-order is bumped to "Hold For Payment". Falls back to global HoldForPaymentOrderStatusId then name lookup. */
		hold_for_payment_orderstatusid?: number | string | null;
		/** Payment terms codes (paymentterms.termsindicator) that should always be treated as must-pay (force Hold For Payment when no payment attached). */
		must_pay_payterms?: string[];
		/** Payment terms codes (paymentterms.termsindicator) that should always be treated as net/on-account (allow split without payment). */
		net_payterms?: string[];
		/** PGS status sync: map queue state to ERP order_status_id */
		pgs_sync_erp_status_ids?: { [queueState: string]: number };
		/** PGS status sync: keyword-based mapping (higher priority than state map) */
		pgs_sync_erp_keyword_status_ids?: Array<{
			match?: string | string[];
			keywords?: string[];
			keyword?: string;
			order_status_id: number;
		}>;
		/** PGS status sync: monotonic rank order of statuses (prevents downgrades) */
		pgs_sync_erp_status_rank?: number[];
		/** PGS status sync: ERP statuses that block PGS-driven updates (except cancellation) */
		pgs_sync_erp_skip_when_orderstatusid?: number[];
		/**
		 * How credits / open receipts (debtortrans 11/12) may be consumed during 3PL shipment invoicing.
		 * Legacy flat keys on config_json are still read by the server if present; new saves use this object only.
		 */
		/** Legacy flat key fallback. Prefer `payment_usage.apply_customer_ar_on_invoice`. */
		apply_customer_ar_on_invoice?: boolean;
		/** Legacy flat key fallback. Prefer `payment_usage.ar_auto_apply_types`. */
		ar_auto_apply_types?: number[];
		/** Legacy flat key fallback. Prefer `payment_usage.allow_manual_open_payment_application`. */
		allow_manual_open_payment_application?: boolean;
		/** Legacy flat key fallback. Prefer `payment_usage.credit_branch_scope`. */
		credit_branch_scope?: 'branch' | 'debtor';
		/** Legacy flat key fallback. Prefer `payment_usage.credit_max_auto_apply_amount`. */
		credit_max_auto_apply_amount?: number | string | null;
		payment_usage?: {
			apply_customer_ar_on_invoice?: boolean;
			ar_auto_apply_types?: number[];
			allow_manual_open_payment_application?: boolean;
			credit_branch_scope?: 'branch' | 'debtor';
			credit_max_auto_apply_amount?: number | null;
		};
		shipment_invoice_worker_enabled?: boolean;
		worker_stage2_enabled?: boolean;
		worker_stage3_enabled?: boolean;
		worker_auto_attach_card?: boolean;
		worker_max_per_run?: number;
		worker_stage2_sleep_ms?: number;
		worker_stage3_min_ship_age_days?: number;
	};
}

export interface Tpl3plSplitBackorderLine {
	orderlineno: number;
	stkcode: string;
	/** Line description: order itemdesc, else stockmaster description */
	description?: string;
	/** Stockmaster mbflag (trimmed, uppercased in API when known) */
	mbflag?: string;
	/** True when mbflag is K: fillability does not consume the SKU sim. pool for this line */
	fillability_kit_header?: boolean;
	/** SUM(locstock.quantity) at fillability warehouse; always >= 0 in API (0 if unknown / none) */
	qoh: number;
	remaining: number;
	simulated_available: number | null;
	/** min(remaining, max(0, simulated_available)) when sim present */
	fillable_qty?: number | null;
	tpl3pl_export_eligible?: boolean;
}

export interface Tpl3plSplitBackorderPreviewResponse {
	success: boolean;
	message?: string;
	error_code?: string;
	source_orderno?: string;
	fillability_bucket?: string;
	fillability_loccode?: string;
	split_applicable?: boolean;
	no_op?: boolean;
	lines_stay?: Tpl3plSplitBackorderLine[];
	lines_move?: Tpl3plSplitBackorderLine[];
	move_linenos?: number[];
	picking_blocked?: boolean;
	tree_blocked?: boolean;
	/** Source order: notes contain a prior 3PL split */
	split_executed?: boolean;
	/** Latest back-order SO # parsed from split note */
	split_backorder_orderno?: string | null;
	/** Back-order SO: customerref BO&lt;parent&gt; */
	split_parent_orderno?: string | null;
	/** True when split was allowed despite non-partial fillability (PGS back order / cancel-at-warehouse queue state). */
	relaxed_fillability_bucket?: boolean;
}

export interface Tpl3plSplitBackorderExecuteResponse {
	success: boolean;
	message?: string;
	error_code?: string;
	source_orderno?: string;
	new_orderno?: string | null;
	moved_line_count?: number;
	no_op?: boolean;
	payment_totals_stale_warning?: boolean;
	preauth_voided?: boolean;
	backorder_payment_method_attached?: boolean;
}

/** Uninvoiced open-lines split preview line (qty / invoiced / remainder). */
export interface Tpl3plSplitUninvoicedLine {
	orderlineno: number;
	stkcode: string;
	description?: string;
	quantity?: number;
	qtyinvoiced?: number;
	remaining?: number;
	move_mode?: 'whole_line' | 'split_remainder';
	stays_invoiced_qty?: number;
	moves_open_qty?: number;
}

export interface Tpl3plSplitUninvoicedOpenLinesPreviewResponse {
	success: boolean;
	message?: string;
	error_code?: string;
	source_orderno?: string;
	split_applicable?: boolean;
	no_op?: boolean;
	lines_stay?: Tpl3plSplitUninvoicedLine[];
	lines_move?: Tpl3plSplitUninvoicedLine[];
	move_whole_linenos?: number[];
	split_lines?: Array<{ orderlineno: number; quantity: number; qtyinvoiced: number; remaining: number }>;
	picking_blocked?: boolean;
	tree_blocked?: boolean;
	has_kit_structure?: boolean;
}

export interface Tpl3plSplitUninvoicedOpenLinesExecuteResponse {
	success: boolean;
	message?: string;
	error_code?: string;
	source_orderno?: string;
	new_orderno?: string | null;
	moved_line_count?: number;
	split_line_count?: number;
	no_op?: boolean;
	payment_totals_stale_warning?: boolean;
	preauth_voided?: boolean;
	backorder_payment_method_attached?: boolean;
	backorder_held_for_payment?: boolean;
	backorder_orderstatusid?: number;
}

/** Row from tpl3pl/orders/failedExportMissingSkus (ERP enrichment for SKUs parsed from failed exports). */
export interface FailedExportMissingSkuReportRow {
	sku: string;
	stockmaster: {
		stockid: string;
		description: string;
		longdescription: string;
		mbflag: string;
		units: string;
		discontinued: number;
	} | null;
	locstock: Array<{
		loccode: string;
		bin: string;
		quantity: number;
		commited: number;
		reorderlevel: number;
	}>;
	ordernos: string[];
	queue_ids: number[];
}

export interface ExportQueueItem {
	id: number;
	orderno: string;
	export_status: 'pending' | 'exporting' | 'exported' | 'failed' | 'dead_letter';
	export_attempts: number;
	last_export_attempt: string;
	export_response: any;
	error_message: string;
	created_at: string;
	exported_at: string;
	archived_at?: string | null;
	/** Queue state machine value from tpl3pl_order_queue.state */
	state?: string | null;
	tpl3pl_remote_order_id?: string | null;
	erp_order_status_id?: string | null;
	erp_order_status_name?: string | null;
	erp_fillability?: string | null;
	erp_fillability_label?: string | null;
	/** Lines where sim. QOH covers outstanding at global walk position (same model as Fillability). */
	erp_fill_lines_covered?: number | null;
	/** Stock-consuming open lines for this order in the fillability walk. */
	erp_fill_lines_open?: number | null;
	debtorno?: string | null;
	branchcode?: string | null;
	/** debtorsmaster.name */
	customer_name?: string | null;
	/** salesorders.payterms — payment terms code (termsindicator) */
	payterms?: string | null;
	/** Display: name (code) from salesorders.salesperson + salesman table */
	placedby?: string | null;
	/** salesorders.salesperson */
	salesperson?: string | null;
	salesperson_name?: string | null;
	enteredby?: string | null;
	/** Last raw PGS status response JSON */
	pgs_status_raw?: string | null;
	/** When PGS status was last polled */
	pgs_status_checked_at?: string | null;
	/** salesorders notes include a 3PL split (back-order child created) */
	tpl3pl_split_executed?: boolean;
	tpl3pl_split_backorder_orderno?: string | null;
	/** This row is a BO split from the given parent SO */
	tpl3pl_split_parent_orderno?: string | null;
	/** Full open SO total (uninvoiced remainder) used for pre-export card hold checks */
	export_open_order_total?: number | null;
	/** Sum of active gateway authorizations on this order when PreAuth is enabled */
	export_preauth_held_total?: number | null;
	/** True when PreAuth is on and open total does not match active holds (e.g. after BO split void) */
	export_needs_preauth?: boolean;
	export_preauth_message?: string | null;
	/** True when payment terms require CC/prepay and active auths do not cover open balance */
	export_needs_cc_payment?: boolean;
	export_cc_payment_message?: string | null;
}

/** Fillability breakdown for non-exported rows still on the 3PL queue (same buckets as the grid). */
export interface Tpl3plOpenQueueFillabilityStats {
	fillable: number;
	partial: number;
	not_fillable: number;
	unknown: number;
	open_queue_orders: number;
}

/** Aggregate queue row counts (non-archived rows by export_status, plus archived exported). */
export interface Tpl3plQueueStatsCounts {
	pending: number;
	exporting: number;
	exported: number;
	/** Non-archived exported rows with no `tpl3pl_shipments` row for the order yet (older APIs omit). */
	exported_not_shipped?: number;
	failed: number;
	dead_letter: number;
	total_active: number;
	/** Pending + failed (typical “needs attention / process queue” work). */
	actionable: number;
	archived_exported: number;
	open_queue_fillability: Tpl3plOpenQueueFillabilityStats;
}

export interface Tpl3plSimulatedAvailabilityLine {
	orderno: string;
	orderlineno: string;
	type: number;
	stkcode: string;
	simulated_available: number;
	/** Total locstock at fillability warehouse for this SKU (before line consumption in the walk) */
	physical_qoh?: number;
}

export interface Tpl3plSimulatedAvailabilityResponse {
	success: boolean;
	orderno: string;
	fillability_loccode: string;
	lines: Tpl3plSimulatedAvailabilityLine[];
	message?: string;
}

export interface Shipment {
	id: number;
	orderno: string;
	tracking_number: string;
	carrier: string;
	service_type?: string;
	ship_date: string;
	delivery_date: string;
	status: string;
	raw_data: any;
	processed: boolean;
	processed_at: string;
	processed_phase?: 'pre_invoice' | 'auto_invoiced' | 'invoice_failed' | 'pending_review';
	invoice_message?: string;
	invoice_status?: 'pending' | 'approved' | 'invoicing' | 'invoiced' | 'failed' | 'rejected' | 'on_hold';
	/** Why an invoice failed/parked; 'sku_mismatch' = shipped code not on the order (resolvable via SKU alias). */
	invoice_failure_type?: 'sku_mismatch' | 'preauth' | 'payment' | 'invoice' | 'other' | null;
	invoice_attempts?: number;
	last_invoice_attempt?: string | null;
	approved_by?: string | null;
	approved_at?: string | null;
	rejected_reason?: string | null;
	erp_status_id?: number | string | null;
	created_at: string;
	// Joined from debtortrans / authorizations
	invoice_transno?: string | null;
	invoice_date?: string | null;
	invoice_total?: number | null;
	active_auth_total?: number | null;
	uncaptured_auth_id?: string | null;
	uncaptured_transid?: string | null;
	uncaptured_card_id?: string | null;
	/** Latest auth row on the order (any status — shows C after gateway capture). */
	last_auth_amount?: number | null;
	last_auth_status?: string | null;
	last_auth_transid?: string | null;
	/** Auth that still needs ERP capture/cc_transactions rows (Stripe may already be settled). */
	reconcile_auth_id?: string | null;
	reconcile_transid?: string | null;
	reconcile_auth_amount?: number | null;
	reconcile_auth_status?: string | null;
	debtorno?: string;
	branchcode?: string;
	/** Customer name from debtorsmaster (list join). */
	customer_name?: string;
	/** Payment terms code from salesorders.payterms (e.g. COD, 30). */
	payterms?: string | null;
}

export interface ShipmentStatsCounts {
	pending: number;
	approved: number;
	invoicing: number;
	invoiced: number;
	failed: number;
	rejected: number;
	on_hold: number;
	total: number;
	actionable: number;
	/** Shipped 3PL rows with no sales invoice for the order. */
	missing_debtortrans_10?: number;
}

export interface InventoryItem {
	id: number;
	stockid: string;
	warehouse_code: string;
	pgs_warehouse?: string;
	quantity_on_hand: number;
	quantity_available: number;
	internal_qoh: number;
	on_demand: number;
	last_sync: string;
	sync_status: 'synced' | 'pending' | 'error';
	error_message: string;
	created_at: string;
	updated_at: string;
	lots?: Array<{
		lot_number: string;
		quantity: number;
		expiration_date: string | null;
		lot_status: string;
		last_sync: string;
	}>;
}

export interface LogEntry {
	id: number;
	log_type: 'order_export' | 'shipment_import' | 'inventory_sync' | 'error' | 'inbound';
	orderno: string;
	stockid: string;
	direction: 'outbound' | 'inbound';
	status: 'success' | 'error' | 'warning';
	message: string;
	request_data: any;
	response_data: any;
	error_details: string;
	created_at: string;
}

export interface InboundPoStatus {
	orderNo: string;
	sent: boolean;
	latest_attempt?: any;
	latest_success?: any;
	stored_inbound_response?: any;
	pgsInboundID?: string | number | null;
	attempt_count?: number;
	purchase_order_exists?: boolean;
}

/** Inventory rollup from tpl3pl_inventory for manual SKU push list. */
export interface Tpl3plSkuPushWarehouseRow {
	warehouse_code: string;
	sync_status: string;
	quantity_available: number;
	quantity_on_hand: number;
}

/** Rollup from tpl3pl_inventory only (PGS cache). ERP locstock / stockmoves are not mixed in here. */
export interface Tpl3plSkuPushInventory {
	state: 'none' | 'synced' | 'error' | 'mixed' | 'pending' | 'unknown';
	has_synced: boolean;
	has_error: boolean;
	has_pending: boolean;
	warehouse_count: number;
	last_inventory_sync: string | null;
	total_qty_available: number;
	total_qty_on_hand: number;
	warehouses: Tpl3plSkuPushWarehouseRow[];
}

export interface Tpl3plSkuPushListItem {
	stockid: string;
	description: string;
	/** stockmaster.date_created from ERP (when present). */
	date_created?: string | null;
	inventory?: Tpl3plSkuPushInventory;
}

export interface Tpl3plSkuPushListResponse {
	success: boolean;
	message?: string;
	stockids: string[];
	items?: Tpl3plSkuPushListItem[];
	total: number;
}

/** Response from tpl3pl/sku/push_preview (single SKU or bulk outbound preview). */
export interface SkuPushPreviewResponse {
	success: boolean;
	message?: string;
	stockid?: string;
	request_payload?: { method?: string; path?: string; body?: Record<string, unknown>; description?: string };
	meta?: {
		integration?: string;
		http?: string;
		mode?: string;
		total_in_scope?: number;
		offset?: number | null;
		limit?: number | null;
		returned?: number;
		skipped_count?: number;
		rows_fetched?: number;
		has_more?: boolean;
		next_offset?: number | null;
		/** Present on client-merged multi-page exports. */
		note?: string;
		filters?: Record<string, unknown>;
		export_merged_pages?: number;
		row_hint?: number;
	};
	requests?: Array<{
		stockid?: string;
		method?: string;
		path?: string;
		body?: Record<string, unknown>;
		description?: string;
	}>;
	skipped?: Array<{ stockid: string; reason: string }>;
}

@Injectable({
	providedIn: 'root'
})
export class Tpl3plService {
	private config: any;
	user: any = [];
	private configSavedSource = new Subject<void>();
	configSaved$ = this.configSavedSource.asObservable();

	constructor(
		private http: HttpClient,
		private globalSearchService: GlobalSearchService,
		private configSvc: RuntimeConfigLoaderService
	) {
		this.globalSearchService.configsubscription.subscribe(r => {
			this.config = r;
		});

		this.globalSearchService.user.subscribe((result) => {
			this.user = result;
		});
	}

	notifyConfigSaved(): void {
		this.configSavedSource.next();
	}

	private setEndPoint(method: string): string {
		return this.getApi() + method + this.getApiKey();
	}

	private getApi(): string {
		return this.config.apiServer.endpoint;
	}

	private getApiKey(): string {
		return '&key=' + this.config.apiCredentials.clientId + '&userid=' + this.user.user.userid + this.getPackage();
	}

	private getPackage(): string {
		const p = this.globalSearchService.getSessionItem('package');
		if (p) {
			return '&package=' + p;
		} else {
			return '';
		}
	}

	private normalizeExportResponse(body: any): { success: boolean; message?: string; response?: any; request_payload?: any } {
		const payload = body?.request_payload;
		const withPayload = (o: { success: boolean; message?: string; response?: any }) =>
			payload !== undefined ? { ...o, request_payload: payload } : o;
		if (body && typeof body.success === 'boolean') {
			return withPayload({
				success: body.success,
				message: body.message,
				response: body.response ?? body.data
			});
		}
		const code = body?.code;
		const data = body?.data;
		if (code >= 200 && code < 300 && data) {
			const details = data.detailsLoaded ?? data.data?.detailsLoaded;
			if (Array.isArray(details)) {
				const msgs: string[] = [];
				for (const d of details) {
					const m = d?.msg && String(d.msg).trim();
					if (m) {
						const sku = d.sku ? String(d.sku) + ': ' : '';
						msgs.push(sku + m);
					}
				}
				if (msgs.length > 0) {
					return withPayload({
						success: false,
						message: 'Order exported with line-level errors: ' + msgs.slice(0, 5).join('; '),
						response: data
					});
				}
			}
			return withPayload({
				success: true,
				message: body.message ?? 'Order exported successfully',
				response: data
			});
		}
		return withPayload({
			success: false,
			message: body?.message ?? body?.error ?? 'Export failed',
			response: body?.data ?? body?.response ?? body
		});
	}

	getConfig(clientId?: string): Observable<Tpl3plConfig> {
		let method = 'tpl3pl/config/get';
		if (clientId) {
			method += '&client_id=' + clientId;
		}
		return this.http.get<Tpl3plConfig>(this.setEndPoint(method));
	}

	saveConfig(config: Tpl3plConfig): Observable<any> {
		const method = 'tpl3pl/config/save';
		return this.http.post(this.setEndPoint(method), config);
	}

	testConnection(config?: Tpl3plConfig): Observable<any> {
		const method = 'tpl3pl/config/test';
		const body = config || {};
		return this.http.post(this.setEndPoint(method), body);
	}

	getUniquePgsStatuses(): Observable<any> {
		const method = 'tpl3pl/pgsstatuses/getUniqueStatuses';
		return this.http.get(this.setEndPoint(method));
	}

	exportOrder(
		orderno: string,
		forceResend?: boolean,
		overrideCcPaymentGate?: boolean,
		overridePreauthGate?: boolean,
		overrideAvailabilityGate?: boolean,
		overrideShippedStatus?: boolean
	): Observable<{ success: boolean; message?: string; response?: any; request_payload?: any }> {
		const method = 'tpl3pl/orders/export';
		const body: {
			orderno: string;
			force_resend?: boolean;
			override_cc_payment_gate?: boolean;
			override_preauth_gate?: boolean;
			override_availability_gate?: boolean;
			override_shipped_status?: boolean;
		} = { orderno };
		if (forceResend) {
			body.force_resend = true;
		}
		if (overrideCcPaymentGate) {
			body.override_cc_payment_gate = true;
		}
		if (overridePreauthGate) {
			body.override_preauth_gate = true;
		}
		if (overrideAvailabilityGate) {
			body.override_availability_gate = true;
		}
		if (overrideShippedStatus) {
			body.override_shipped_status = true;
		}
		return this.http.post(this.setEndPoint(method), body).pipe(
			map((body: any) => this.normalizeExportResponse(body))
		);
	}

	getExportQueue(filters?: {
		/** Single status (backward compatible; ignored if `statuses` is non-empty). */
		status?: string;
		/** Multiple export_status values (AND with other filters). */
		statuses?: string[];
		/** Multiple tpl3pl_order_queue.state values. */
		states?: string[];
		limit?: number;
		offset?: number;
		includeArchived?: boolean;
		/** Exact ERP order number. */
		orderno?: string;
		/** Substring match on orderno (SQL LIKE). */
		orderno_contains?: string;
		/** Substring match on pgs_order_id. */
		pgs_order_id_contains?: string;
		/** Substring match on error_message. */
		error_contains?: string;
		/** Substring match on debtor name, debtor #, branch, or customer ref (server EXISTS on salesorders + debtorsmaster). */
		customer_contains?: string;
		/** ERP sales order status IDs (salesorders.orderstatusid). */
		erp_status_ids?: number[];
		/** salesorders.salesperson (salesmancode), exact match after trim; comma-separated on the wire. */
		salesperson?: string[];
		/** Server: fillable | partial | not_fillable | unknown (comma list). Scans a larger window then filters. */
		fillabilities?: string[];
		/** Comma-separated order numbers: queue list restricted to these SOs (max 500), AND with other filters. */
		orderno_in?: string;
	}): Observable<{
		success: boolean;
		data: ExportQueueItem[];
		total: number;
		limit: number;
		offset: number;
		fillability_scan_capped?: boolean;
	}> {
		const method = 'tpl3pl/orders/queue';
		const body: Record<string, unknown> = {};
		if (filters) {
			if (filters.limit != null) {
				body.limit = filters.limit;
			}
			if (filters.offset != null) {
				body.offset = filters.offset;
			}
			if (filters.includeArchived) {
				body.include_archived = true;
			}
			if (filters.statuses && filters.statuses.length > 0) {
				body.statuses = [...filters.statuses];
			} else if (filters.status && filters.status.trim() !== '') {
				body.status = filters.status.trim();
			}
			if (filters.states && filters.states.length > 0) {
				body.states = [...filters.states];
			}
			if (filters.orderno && filters.orderno.trim() !== '') {
				body.orderno = filters.orderno.trim();
			}
			if (filters.orderno_contains && filters.orderno_contains.trim() !== '') {
				body.orderno_contains = filters.orderno_contains.trim();
			}
			if (filters.pgs_order_id_contains && filters.pgs_order_id_contains.trim() !== '') {
				body.pgs_order_id_contains = filters.pgs_order_id_contains.trim();
			}
			if (filters.error_contains && filters.error_contains.trim() !== '') {
				body.error_contains = filters.error_contains.trim();
			}
			if (filters.customer_contains && filters.customer_contains.trim() !== '') {
				body.customer_contains = filters.customer_contains.trim();
			}
			if (filters.erp_status_ids && filters.erp_status_ids.length > 0) {
				const ids = [...new Set(filters.erp_status_ids.filter((n) => n > 0))];
				if (ids.length > 0) {
					body.erp_status_ids = ids;
				}
			}
			if (filters.salesperson && filters.salesperson.length > 0) {
				const codes = [
					...new Set(
						filters.salesperson
							.map((x) => String(x).trim())
							.filter((x) => x !== '')
					)
				].slice(0, 40);
				if (codes.length > 0) {
					body.salesperson = codes;
				}
			}
			if (filters.fillabilities && filters.fillabilities.length > 0) {
				const f = [...new Set(filters.fillabilities.map((x) => String(x).trim()).filter((x) => x !== ''))];
				if (f.length > 0) {
					body.fillabilities = f;
				}
			}
			if (filters.orderno_in && filters.orderno_in.trim() !== '') {
				body.orderno_in = filters.orderno_in.trim();
			}
		}
		return this.http.post<{
			success: boolean;
			data: ExportQueueItem[];
			total: number;
			limit: number;
			offset: number;
			fillability_scan_capped?: boolean;
		}>(this.setEndPoint(method), body);
	}

	getQueueStats(): Observable<{ success: boolean; counts: Tpl3plQueueStatsCounts }> {
		return this.http.get<{ success: boolean; counts: Tpl3plQueueStatsCounts }>(
			this.setEndPoint('tpl3pl/orders/queueStats')
		);
	}

	/**
	 * Scan failed/dead-letter queue rows for missing-SKU errors; enrich with stockmaster + locstock.
	 */
	getFailedExportMissingSkus(params?: {
		days?: number;
		limit?: number;
		include_archived?: boolean;
	}): Observable<{
		success: boolean;
		message?: string;
		generated_at?: string;
		queue_rows_scanned?: number;
		unique_sku_count?: number;
		csv?: string;
		skus?: FailedExportMissingSkuReportRow[];
	}> {
		let method = 'tpl3pl/orders/failedExportMissingSkus';
		if (params) {
			if (params.days != null) {
				method += '&days=' + encodeURIComponent(String(params.days));
			}
			if (params.limit != null) {
				method += '&limit=' + encodeURIComponent(String(params.limit));
			}
			if (params.include_archived) {
				method += '&include_archived=1';
			}
		}
		return this.http.get<{
			success: boolean;
			message?: string;
			generated_at?: string;
			queue_rows_scanned?: number;
			unique_sku_count?: number;
			csv?: string;
			skus?: FailedExportMissingSkuReportRow[];
		}>(this.setEndPoint(method));
	}

	getSimulatedLineAvailability(orderno: string): Observable<Tpl3plSimulatedAvailabilityResponse> {
		const method = 'tpl3pl/orders/simulatedLineAvailability&orderno=' + encodeURIComponent(orderno);
		return this.http.get<Tpl3plSimulatedAvailabilityResponse>(this.setEndPoint(method));
	}

	retryExport(
		id?: number,
		orderno?: string,
		forceResend?: boolean,
		overrideCcPaymentGate?: boolean,
		overridePreauthGate?: boolean,
		overrideAvailabilityGate?: boolean
	): Observable<{ success: boolean; message?: string; response?: any }> {
		const method = 'tpl3pl/orders/retry';
		const body: {
			id?: number;
			orderno?: string;
			force_resend?: boolean;
			override_cc_payment_gate?: boolean;
			override_preauth_gate?: boolean;
			override_availability_gate?: boolean;
		} = {};
		if (id) {body.id = id;}
		if (orderno) {body.orderno = orderno;}
		if (forceResend) {body.force_resend = true;}
		if (overrideCcPaymentGate) {body.override_cc_payment_gate = true;}
		if (overridePreauthGate) {body.override_preauth_gate = true;}
		if (overrideAvailabilityGate) {body.override_availability_gate = true;}
		return this.http.post(this.setEndPoint(method), body).pipe(
			map((res: any) => this.normalizeExportResponse(res))
		);
	}

	getPgsOrderStatus(orderno: string): Observable<{ success: boolean; data?: any; message?: string; channel_used?: string; orderno?: string; b2c_error?: string; b2b_error?: string }> {
		return this.http.post<any>(this.setEndPoint('tpl3pl/orders/pgsOrderStatus'), { orderno });
	}

	massUpdatePgsStatus(
		limit = 25,
		offset = 0
	): Observable<{
		success: boolean;
		message?: string;
		checked: number;
		updated: number;
		unchanged: number;
		failed: number;
		details: any[];
		erp_status_updated?: number;
		offset?: number;
		limit?: number;
		has_more?: boolean;
	}> {
		return this.http.post<any>(this.setEndPoint('tpl3pl/orders/massUpdatePgsStatus'), { limit, offset });
	}

	cancelOrderPreview(orderno: string): Observable<{ success: boolean; request_payload?: any; channel?: string }> {
		return this.http.post<{ success: boolean; request_payload?: any; channel?: string }>(this.setEndPoint('tpl3pl/orders/cancelPreview'), { orderno });
	}

	cancelOrder(
		orderno: string,
		opts?: { sync_erp_cancel?: boolean; prepare_split_resubmit_after_release?: boolean }
	): Observable<{
		success: boolean;
		message: string;
		request_payload?: any;
		response?: any;
		pgs_result?: any;
		queue_updated?: boolean;
		prepare_split_resubmit?: { success?: boolean; message?: string } | null;
	}> {
		const body: { orderno: string; sync_erp_cancel?: boolean; prepare_split_resubmit_after_release?: boolean } = { orderno };
		if (opts && typeof opts.sync_erp_cancel === 'boolean') {
			body.sync_erp_cancel = opts.sync_erp_cancel;
		}
		if (opts && typeof opts.prepare_split_resubmit_after_release === 'boolean') {
			body.prepare_split_resubmit_after_release = opts.prepare_split_resubmit_after_release;
		}
		return this.http.post<{
			success: boolean;
			message: string;
			pgs_result?: any;
			queue_updated?: boolean;
			pgs_not_supported?: boolean;
			prepare_split_resubmit?: { success?: boolean; message?: string } | null;
		}>(
			this.setEndPoint('tpl3pl/orders/cancel'),
			body
		);
	}

	deleteQueueItem(id: number): Observable<{ success: boolean; message?: string }> {
		const method = 'tpl3pl/orders/delete';
		return this.http.post<{ success: boolean; message?: string }>(this.setEndPoint(method), { id });
	}

	archiveQueueItem(id: number): Observable<{ success: boolean; message?: string }> {
		return this.http.post<{ success: boolean; message?: string }>(this.setEndPoint('tpl3pl/orders/archive'), { id });
	}

	unarchiveQueueItem(id: number, orderno?: string): Observable<{ success: boolean; message?: string; orderno?: string }> {
		const body: { id?: number; orderno?: string } = {};
		if (id) { body.id = id; }
		if (orderno) { body.orderno = orderno; }
		return this.http.post<{ success: boolean; message?: string; orderno?: string }>(this.setEndPoint('tpl3pl/orders/unarchive'), body);
	}

	/** Failed/dead-letter only: mark as exported + Dispatched (e.g. duplicate already at 3PL). */
	markQueueDispatched(id: number): Observable<{ success: boolean; message?: string; id?: number; orderno?: string }> {
		return this.http.post<{ success: boolean; message?: string; id?: number; orderno?: string }>(
			this.setEndPoint('tpl3pl/orders/markDispatched'),
			{ id }
		);
	}

	archiveAllExported(): Observable<{ success: boolean; message?: string; archived_count?: number }> {
		return this.http.post<{ success: boolean; message?: string; archived_count?: number }>(this.setEndPoint('tpl3pl/orders/archiveAllExported'), {});
	}

	/**
	 * Archive exported queue rows whose state is Shipped (PGS reported shipped via mass sync / worker).
	 * @param includeInvoiced Also archive rows in Invoiced state.
	 */
	archivePgsShippedQueue(includeInvoiced?: boolean): Observable<{
		success: boolean;
		message?: string;
		archived_count?: number;
		include_invoiced?: boolean;
	}> {
		const body: { include_invoiced?: boolean } = {};
		if (includeInvoiced) {
			body.include_invoiced = true;
		}
		return this.http.post<{
			success: boolean;
			message?: string;
			archived_count?: number;
			include_invoiced?: boolean;
		}>(this.setEndPoint('tpl3pl/orders/archivePgsShippedQueue'), body);
	}

	getExportPreview(orderno: string): Observable<{
		success: boolean;
		request_payload?: any;
		message?: string;
		error_code?: string;
		export_line_breakdown?: {
			lines: Array<{
				orderlineno?: number | null;
				parentlineno?: number | null;
				stkcode: string;
				quantity?: number | null;
				included: boolean;
				exclude_reason?: string | null;
				warning?: string | null;
				stockmaster_type_code?: string | null;
				product_type_label?: string | null;
				product_type_description?: string | null;
				has_product_types_row?: boolean;
			}>;
			included_line_count: number;
			excluded_line_count: number;
		};
		export_preauth?: {
			blocked?: boolean;
			open_order_total?: number | null;
			preauth_total?: number | null;
			preauth_checked?: boolean;
			preauth_hold_unavailable?: boolean;
			message?: string;
		};
		export_cc_payment_gate?: {
			blocked?: boolean;
			open_order_total?: number | null;
			auth_held_total?: number | null;
			message?: string;
		};
	}> {
		return this.http.post<{ success: boolean; request_payload?: any; message?: string }>(
			this.setEndPoint('tpl3pl/orders/preview'),
			{ orderno }
		);
	}

	/** New card hold for full open order amount (pre-export / queue), same backend path as shipment re-auth but full SO remainder. */
	reauthorizePreExport(orderno: string): Observable<{
		success: boolean;
		message?: string;
		trans_id?: string;
		void_attempted?: number;
	}> {
		return this.http.post<{
			success: boolean;
			message?: string;
			trans_id?: string;
			void_attempted?: number;
		}>(this.setEndPoint('tpl3pl/orders/reauthorizePreExport'), { orderno: (orderno || '').trim() });
	}

	splitBackorderPreview(orderno: string): Observable<Tpl3plSplitBackorderPreviewResponse> {
		return this.http.post<Tpl3plSplitBackorderPreviewResponse>(
			this.setEndPoint('tpl3pl/orders/splitBackorderPreview'),
			{ orderno: (orderno || '').trim() }
		);
	}

	splitBackorderExecute(orderno: string): Observable<Tpl3plSplitBackorderExecuteResponse> {
		return this.http.post<Tpl3plSplitBackorderExecuteResponse>(
			this.setEndPoint('tpl3pl/orders/splitBackorderExecute'),
			{ orderno: (orderno || '').trim() }
		);
	}

	splitUninvoicedOpenLinesPreview(orderno: string): Observable<Tpl3plSplitUninvoicedOpenLinesPreviewResponse> {
		return this.http.post<Tpl3plSplitUninvoicedOpenLinesPreviewResponse>(
			this.setEndPoint('tpl3pl/orders/splitUninvoicedOpenLinesPreview'),
			{ orderno: (orderno || '').trim() }
		);
	}

	splitUninvoicedOpenLinesExecute(orderno: string): Observable<Tpl3plSplitUninvoicedOpenLinesExecuteResponse> {
		return this.http.post<Tpl3plSplitUninvoicedOpenLinesExecuteResponse>(
			this.setEndPoint('tpl3pl/orders/splitUninvoicedOpenLinesExecute'),
			{ orderno: (orderno || '').trim() }
		);
	}

	/** After PGS cancel or queue back-order: unarchive, pending, clear PGS id + idempotency so you can split and export again. */
	prepareSplitResubmit(orderno: string): Observable<{ success: boolean; message?: string }> {
		return this.http.post<{ success: boolean; message?: string }>(
			this.setEndPoint('tpl3pl/orders/prepareSplitResubmit'),
			{ orderno: (orderno || '').trim() }
		);
	}

	/** PGS cancel (old #) → next SO # in ERP + repoint details/payment/queue; then split/export under new #. */
	bumpOrdernoFor3plResubmit(
		orderno: string,
		opts?: { skip_pgs_cancel?: boolean; ignore_pgs_cancel_failure?: boolean }
	): Observable<{
		success: boolean;
		message?: string;
		error_code?: string;
		old_orderno?: string;
		new_orderno?: string;
		pgs_cancel?: unknown;
	}> {
		const body: { orderno: string; skip_pgs_cancel?: boolean; ignore_pgs_cancel_failure?: boolean } = {
			orderno: (orderno || '').trim(),
		};
		if (opts?.skip_pgs_cancel) {
			body.skip_pgs_cancel = true;
		}
		if (opts?.ignore_pgs_cancel_failure) {
			body.ignore_pgs_cancel_failure = true;
		}
		return this.http.post<{
			success: boolean;
			message?: string;
			error_code?: string;
			old_orderno?: string;
			new_orderno?: string;
			pgs_cancel?: unknown;
		}>(this.setEndPoint('orders/bumpOrdernoFor3plResubmit'), body);
	}

	processQueue(limit?: number): Observable<any> {
		const method = 'tpl3pl/orders/process';
		const body: any = {};
		if (limit) { body.limit = limit; }
		return this.http.post(this.setEndPoint(method), body);
	}

	/** Direct salesorders.orderstatusid update (no dispatch/picking side effects). */
	setErpOrderStatus(orderno: string, orderStatusId: number): Observable<{
		success: boolean;
		message?: string;
		erp_order_status_id?: string;
		erp_order_status_name?: string;
	}> {
		return this.http.post<{
			success: boolean;
			message?: string;
			erp_order_status_id?: string;
			erp_order_status_name?: string;
		}>(this.setEndPoint('tpl3pl/orders/setErpOrderStatus'), {
			orderno: (orderno || '').trim(),
			order_status_id: orderStatusId,
		});
	}

	importShipments(data: any): Observable<any> {
		const method = 'tpl3pl/shipments/import';
		return this.http.post(this.setEndPoint(method), { data });
	}

	pollShipments(dateFrom?: string, dateTo?: string): Observable<any> {
		const method = 'tpl3pl/shipments/poll';
		const body: any = {};
		if (dateFrom) { body.date_from = dateFrom; }
		if (dateTo) { body.date_to = dateTo; }
		if (!dateFrom && !dateTo) { body.date = new Date().toISOString().slice(0, 10); }
		return this.http.post(this.setEndPoint(method), body, { responseType: 'text' }).pipe(
			map((raw: string) => this.parsePollResponse(raw))
		);
	}

	private parsePollResponse(raw: string): any {
		if (typeof raw !== 'string' || raw.trim() === '') {
			return { success: false, message: 'Empty response', processed: 0 };
		}
		const secondBrace = raw.indexOf('}{');
		const jsonStr = secondBrace >= 0 ? raw.substring(secondBrace + 1) : raw;
		try {
			return JSON.parse(jsonStr);
		} catch {
			try {
				return JSON.parse(raw);
			} catch {
				return { success: false, message: 'Invalid JSON response', processed: 0 };
			}
		}
	}

	pollShipmentsByOrderNo(orderNo: string, channel: 'b2b' | 'b2c' | 'both' = 'both'): Observable<any> {
		const body: { order_no: string; channel?: string } = { order_no: orderNo.trim() };
		if (channel !== 'both') {
			body.channel = channel;
		}
		return this.http.post(this.setEndPoint('tpl3pl/shipments/poll_by_order'), body);
	}

	pollShipmentsRaw(dateFrom?: string, dateTo?: string): Observable<{ success: boolean; data?: any[]; request_payload?: any; response?: any; response_b2b?: any; response_b2c?: any; message?: string }> {
		const method = 'tpl3pl/shipments/poll_raw';
		const body: any = {};
		if (dateFrom) { body.date_from = dateFrom; }
		if (dateTo) { body.date_to = dateTo; }
		if (!dateFrom && !dateTo) { body.date = new Date().toISOString().slice(0, 10); }
		return this.http.post(this.setEndPoint(method), body, { responseType: 'text' }).pipe(
			map((raw: string) => this.parsePollResponse(raw))
		);
	}

	getNextInboundPo(): Observable<{ success: boolean; message?: string; purchaseOrderNo?: string }> {
		return this.http.post<{ success: boolean; message?: string; purchaseOrderNo?: string }>(this.setEndPoint('tpl3pl/inbound/getnextpo'), {});
	}

	getInboundPreview(purchaseOrderNo?: string): Observable<{ success: boolean; message?: string; request_payload?: { method: string; path: string; body: any } }> {
		const body: any = {};
		if (purchaseOrderNo != null && purchaseOrderNo.trim() !== '') { body.purchaseOrderNo = purchaseOrderNo.trim(); }
		return this.http.post<{ success: boolean; message?: string; request_payload?: { method: string; path: string; body: any } }>(this.setEndPoint('tpl3pl/inbound/preview'), body);
	}

	createInbound(payload: any): Observable<any> {
		return this.http.post(this.setEndPoint('tpl3pl/inbound/create'), { payload });
	}

	getInboundReceivedByDate(date: string): Observable<{ success: boolean; data: any[] }> {
		let method = 'tpl3pl/inbound/received';
		method += '&date=' + encodeURIComponent(date);
		return this.http.get<{ success: boolean; data: any[] }>(this.setEndPoint(method));
	}

	getInboundReceivedByPo(orderNo: string): Observable<{ success: boolean; data: any[] }> {
		let method = 'tpl3pl/inbound/receivedpo';
		method += '&orderNo=' + encodeURIComponent(orderNo);
		return this.http.get<{ success: boolean; data: any[] }>(this.setEndPoint(method));
	}

	getInboundPoStatus(orderNo: string): Observable<{ success: boolean; data: InboundPoStatus; history: LogEntry[]; message?: string }> {
		let method = 'tpl3pl/inbound/statuspo';
		method += '&orderNo=' + encodeURIComponent(orderNo);
		return this.http.get<{ success: boolean; data: InboundPoStatus; history: LogEntry[]; message?: string }>(this.setEndPoint(method));
	}

	syncInboundReceived(orderNo?: string): Observable<{ success: boolean; processed: number; skipped: number; errors: number; pos_checked?: number; message?: string }> {
		const body: any = {};
		if (orderNo) { body['orderNo'] = orderNo; }
		return this.http.post<{ success: boolean; processed: number; skipped: number; errors: number; pos_checked?: number; message?: string }>(
			this.setEndPoint('tpl3pl/inbound/syncreceived'), body
		);
	}

	listInboundReceipts(orderNo?: string, limit = 100, offset = 0): Observable<{ success: boolean; data: any[]; total: number }> {
		let method = 'tpl3pl/inbound/listreceipts&limit=' + limit + '&offset=' + offset;
		if (orderNo) { method += '&orderNo=' + encodeURIComponent(orderNo); }
		return this.http.get<{ success: boolean; data: any[]; total: number }>(this.setEndPoint(method));
	}

	getInboundSkuStatus(orderNo: string): Observable<{
		success: boolean;
		orderno: string;
		total_sent: number;
		flagged_count: number;
		resolved_count: number;
		skus: {
			sku: string;
			description: string | null;
			discontinued: boolean | null;
			in_erp: boolean;
			in_3pl: boolean;
			flagged: boolean;
			resolved: boolean;
		}[];
		message?: string;
	}> {
		return this.http.get<any>(this.setEndPoint('tpl3pl/inbound/skustatus&orderNo=' + encodeURIComponent(orderNo)));
	}

	listInboundSends(filters?: {
		orderNo?: string;
		status?: '' | 'sent' | 'errored' | 'received' | 'unreceived';
		from_date?: string;
		to_date?: string;
		limit?: number;
		offset?: number;
	}): Observable<{ success: boolean; data: any[]; total: number }> {
		let method = 'tpl3pl/inbound/listsent';
		const f = filters || {};
		const limit = f.limit ?? 100;
		const offset = f.offset ?? 0;
		method += '&limit=' + limit + '&offset=' + offset;
		if (f.orderNo) { method += '&orderNo=' + encodeURIComponent(f.orderNo); }
		if (f.status) { method += '&status=' + encodeURIComponent(f.status); }
		if (f.from_date) { method += '&from_date=' + encodeURIComponent(f.from_date); }
		if (f.to_date) { method += '&to_date=' + encodeURIComponent(f.to_date); }
		return this.http.get<{ success: boolean; data: any[]; total: number }>(this.setEndPoint(method));
	}

	previewInboundPo(orderNo: string): Observable<{
		success: boolean;
		skipped?: boolean;
		message?: string;
		orderno: string;
		supplier_id?: string;
		supplier_name?: string;
		payload?: any;
		sku_check?: { sku: string; description: string; qty: number; in_3pl: boolean }[];
		total_skus?: number;
		all_skus_ready?: boolean;
		missing_count?: number;
	}> {
		return this.http.get<any>(this.setEndPoint('tpl3pl/inbound/previewpo&orderNo=' + encodeURIComponent(orderNo)));
	}

	sendInboundPo(orderNo: string, forceResend = false): Observable<{
		success: boolean;
		skipped?: boolean;
		already_sent?: boolean;
		message?: string;
		result?: any;
		history?: LogEntry[];
		status?: InboundPoStatus;
	}> {
		return this.http.post<{
			success: boolean;
			skipped?: boolean;
			already_sent?: boolean;
			message?: string;
			result?: any;
			history?: LogEntry[];
			status?: InboundPoStatus;
		}>(this.setEndPoint('tpl3pl/inbound/sendpo'), { orderNo, forceResend });
	}

	getOpenB2BOrders(): Observable<{ success: boolean; data: any[] }> {
		return this.http.get<{ success: boolean; data: any[] }>(this.setEndPoint('tpl3pl/outbound/open'));
	}

	getOpenB2BOrdersRaw(): Observable<{ success: boolean; data: any[]; request_payload?: any; response?: any; message?: string }> {
		return this.http.get<{ success: boolean; data: any[]; request_payload?: any; response?: any; message?: string }>(this.setEndPoint('tpl3pl/outbound/open_raw'));
	}

	/**
	 * Preview JSON body sent to PGS POST /api/Sku (no HTTP to PGS). Single-SKU: pass a string stockid.
	 * Bulk: pass options with offset/limit/search/sort/created_within_days (same as push list) or stockids[].
	 */
	getSkuPushPreview(
		stockidOrOpts?: string | {
			stockid?: string;
			stockids?: string[];
			offset?: number;
			limit?: number;
			search?: string;
			sort?: 'stockid' | 'newest';
			created_within_days?: number;
			/** ERP manufacturer_id; limits list to SKUs in product_to_manufacturer for that brand */
			manufacturer_id?: number;
			/** Same as push_list: only SKUs absent from tpl3pl_inventory */
			not_in_inventory_feed?: boolean;
		}
	): Observable<SkuPushPreviewResponse> {
		const body: Record<string, unknown> = {};
		if (typeof stockidOrOpts === 'string') {
			const s = stockidOrOpts.trim();
			if (s) {
				body.stockid = s;
			}
		} else if (stockidOrOpts && typeof stockidOrOpts === 'object') {
			if (stockidOrOpts.stockid != null && String(stockidOrOpts.stockid).trim() !== '') {
				body.stockid = String(stockidOrOpts.stockid).trim();
			} else {
				if (stockidOrOpts.stockids != null && stockidOrOpts.stockids.length > 0) {
					body.stockids = stockidOrOpts.stockids.map((x) => String(x).trim()).filter(Boolean);
				}
				if (stockidOrOpts.offset != null) {
					body.offset = Math.max(0, Math.floor(Number(stockidOrOpts.offset)));
				}
				if (stockidOrOpts.limit != null) {
					body.limit = Math.min(5000, Math.max(1, Math.floor(Number(stockidOrOpts.limit))));
				}
				if (stockidOrOpts.search != null && String(stockidOrOpts.search).trim() !== '') {
					body.search = String(stockidOrOpts.search).trim();
				}
				if (stockidOrOpts.sort === 'newest') {
					body.sort = 'newest';
				}
				if (stockidOrOpts.created_within_days != null && stockidOrOpts.created_within_days > 0) {
					body.created_within_days = stockidOrOpts.created_within_days;
				}
				if (stockidOrOpts.manufacturer_id != null && stockidOrOpts.manufacturer_id > 0) {
					body.manufacturer_id = Math.floor(Number(stockidOrOpts.manufacturer_id));
				}
				if (stockidOrOpts.not_in_inventory_feed) {
					body.not_in_inventory_feed = true;
				}
			}
		}
		return this.http.post<SkuPushPreviewResponse>(this.setEndPoint('tpl3pl/sku/push_preview'), body);
	}

	/** Single SKU push — no RxJS timeout here (only inventory sync uses a long timeout). Slowness is usually sequential batching + server→PGS latency. */
	pushOneSku(stockid: string): Observable<{ success: boolean; message?: string; request_payload?: any; response?: any; stockid?: string }> {
		return this.http.post<{ success: boolean; message?: string; request_payload?: any; response?: any; stockid?: string }>(this.setEndPoint('tpl3pl/sku/push_one'), { stockid });
	}

	/** Manual SKU push: multiple SKUs in one HTTP round-trip (same PGS logic as push_one per item). Server caps at 100 stockids. */
	pushManualSkuBatch(stockids: string[]): Observable<{
		success: boolean;
		message?: string;
		results?: Array<{ success: boolean; message?: string; request_payload?: any; response?: any; stockid?: string; treated_as_success?: boolean }>;
	}> {
		return this.http.post<{
			success: boolean;
			message?: string;
			results?: Array<{ success: boolean; message?: string; request_payload?: any; response?: any; stockid?: string; treated_as_success?: boolean }>;
		}>(this.setEndPoint('tpl3pl/sku/push_manual_batch'), { stockids });
	}

	getSkuPushList(
		offset: number,
		limit: number,
		search?: string,
		opts?: {
			sort?: 'stockid' | 'newest';
			created_within_days?: number;
			manufacturer_id?: number;
			/** Server: only SKUs with no tpl3pl_inventory rows (not in last inventory pull cache). */
			not_in_inventory_feed?: boolean;
		}
	): Observable<Tpl3plSkuPushListResponse> {
		const body: {
			offset: number;
			limit: number;
			search?: string;
			sort?: string;
			created_within_days?: number;
			manufacturer_id?: number;
			not_in_inventory_feed?: boolean;
		} = { offset, limit };
		if (search != null && search.trim() !== '') {
			body.search = search.trim();
		}
		if (opts?.sort === 'newest') {
			body.sort = 'newest';
		}
		if (opts?.created_within_days != null && opts.created_within_days > 0) {
			body.created_within_days = opts.created_within_days;
		}
		if (opts?.manufacturer_id != null && opts.manufacturer_id > 0) {
			body.manufacturer_id = Math.floor(Number(opts.manufacturer_id));
		}
		if (opts?.not_in_inventory_feed) {
			body.not_in_inventory_feed = true;
		}
		return this.http.post<Tpl3plSkuPushListResponse>(this.setEndPoint('tpl3pl/sku/push_list'), body);
	}

	pushSkuBatch(offset: number, limit: number): Observable<{
		success: boolean;
		created?: number;
		updated?: number;
		failed?: number;
		offset?: number;
		responses?: Array<{ stockid: string; success: boolean; raw_response: any }>;
	}> {
		return this.http.post<{
			success: boolean;
			created?: number;
			updated?: number;
			failed?: number;
			offset?: number;
			responses?: Array<{ stockid: string; success: boolean; raw_response: any }>;
		}>(this.setEndPoint('tpl3pl/sku/push_batch'), { offset, limit });
	}

	createSku(payload: any): Observable<any> {
		return this.http.post(this.setEndPoint('tpl3pl/sku/create'), { payload });
	}

	deleteSku(sku: string): Observable<{ success: boolean; message?: string }> {
		return this.http.post<{ success: boolean; message?: string }>(this.setEndPoint('tpl3pl/sku/delete'), { sku });
	}

	startSkuSync(removeFromPgs?: boolean): Observable<{ success: boolean; job_id?: number; message?: string }> {
		const body: any = {};
		if (removeFromPgs) { body.remove_from_pgs = true; }
		return this.http.post<{ success: boolean; job_id?: number; message?: string }>(this.setEndPoint('tpl3pl/sku/start'), body);
	}

	getSkuSyncStatus(): Observable<{
		success: boolean;
		job: {
			id: number;
			status: string;
			total: number;
			current_offset: number;
			created: number;
			updated: number;
			deleted: number;
			failed: number;
			errors: Array<{ stockid: string; message: string }>;
			remove_from_pgs: boolean;
			phase: string;
			started_at: string | null;
			completed_at: string | null;
			created_at: string;
		} | null;
	}> {
		return this.http.get<{
			success: boolean;
			job: {
				id: number;
				status: string;
				total: number;
				current_offset: number;
				created: number;
				updated: number;
				deleted: number;
				failed: number;
				errors: Array<{ stockid: string; message: string }>;
				remove_from_pgs: boolean;
				phase: string;
				started_at: string | null;
				completed_at: string | null;
				created_at: string;
			} | null;
		}>(this.setEndPoint('tpl3pl/sku/syncStatus'));
	}

	syncSkusToPgs(params?: { offset?: number; phase?: 'sync' | 'remove' }): Observable<{
		success: boolean;
		created?: number;
		updated?: number;
		deleted?: number;
		failed: number;
		errors: Array<{ stockid: string; message: string }>;
		total: number;
		offset: number;
		next_offset?: number;
		done: boolean;
		message?: string;
		phase?: 'sync' | 'remove';
	}> {
		const body = params || {};
		return this.http.post<{
			success: boolean;
			created?: number;
			updated?: number;
			deleted?: number;
			failed: number;
			errors: Array<{ stockid: string; message: string }>;
			total: number;
			offset: number;
			next_offset?: number;
			done: boolean;
			message?: string;
			phase?: 'sync' | 'remove';
		}>(this.setEndPoint('tpl3pl/sku/sync'), body);
	}

	getShipments(filters?: {
		shipment_id?: number;
		orderno?: string;
		invoice_status?: string;
		invoice_statuses?: string[];
		date_from?: string;
		date_to?: string;
		erp_status_ids?: number[];
		/** Shipped rows with no invoice for the order. */
		missing_debtortrans_10?: boolean;
		/** Server whitelist: ship_date, orderno, customer_name, debtorno, payterms, tracking, carrier, invoice_status, invoice_total, created_at, phase, last_auth_amount, attempts */
		sort_by?: string;
		sort_dir?: 'asc' | 'desc';
		limit?: number;
		offset?: number;
	}): Observable<{
		success: boolean;
		data: Shipment[];
		total: number;
		limit: number;
		offset: number;
	}> {
		let method = 'tpl3pl/shipments/list';
		if (filters) {
			if (filters.shipment_id != null && filters.shipment_id > 0) {
				method += '&shipment_id=' + encodeURIComponent(String(filters.shipment_id));
			}
			if (filters.orderno) {method += '&orderno=' + encodeURIComponent(filters.orderno);}
			if (filters.invoice_statuses && filters.invoice_statuses.length > 0) {
				method += '&invoice_statuses=' + encodeURIComponent(filters.invoice_statuses.join(','));
			} else if (filters.invoice_status && filters.invoice_status.trim() !== '') {
				method += '&invoice_status=' + encodeURIComponent(filters.invoice_status.trim());
			}
			if (filters.date_from) {method += '&date_from=' + encodeURIComponent(filters.date_from);}
			if (filters.date_to) {method += '&date_to=' + encodeURIComponent(filters.date_to);}
			if (filters.erp_status_ids && filters.erp_status_ids.length > 0) {
				const ids = [...new Set(filters.erp_status_ids.filter((n) => n > 0))];
				if (ids.length > 0) {
					method += '&erp_status_ids=' + encodeURIComponent(ids.join(','));
				}
			}
			if (filters.missing_debtortrans_10) {
				method += '&missing_debtortrans_10=1';
			}
			if (filters.sort_by && String(filters.sort_by).trim() !== '') {
				method += '&sort_by=' + encodeURIComponent(String(filters.sort_by).trim());
			}
			if (filters.sort_dir === 'asc' || filters.sort_dir === 'desc') {
				method += '&sort_dir=' + encodeURIComponent(filters.sort_dir);
			}
			if (filters.limit != null) {method += '&limit=' + encodeURIComponent(String(filters.limit));}
			if (filters.offset != null) {method += '&offset=' + encodeURIComponent(String(filters.offset));}
		}
		return this.http.get<{
			success: boolean;
			data: Shipment[];
			total: number;
			limit: number;
			offset: number;
		}>(this.setEndPoint(method));
	}

	getShipmentStats(filters?: {
		date_from?: string;
		date_to?: string;
		orderno?: string;
	}): Observable<{ success: boolean; counts: ShipmentStatsCounts }> {
		let method = 'tpl3pl/shipments/shipment_stats';
		if (filters) {
			if (filters.date_from) {
				method += '&date_from=' + encodeURIComponent(filters.date_from);
			}
			if (filters.date_to) {
				method += '&date_to=' + encodeURIComponent(filters.date_to);
			}
			if (filters.orderno) {
				method += '&orderno=' + encodeURIComponent(filters.orderno);
			}
		}
		return this.http.get<{ success: boolean; counts: ShipmentStatsCounts }>(this.setEndPoint(method));
	}

	approveShipment(shipmentId: number, invoiceNow = false): Observable<any> {
		return this.http.post(this.setEndPoint('tpl3pl/shipments/approve'), { shipment_id: shipmentId, invoice_now: invoiceNow });
	}

	approveAndInvoiceShipment(
		shipmentId: number,
		arApplications?: Array<{ id: number; amount: number }>
	): Observable<any> {
		const body: Record<string, unknown> = { shipment_id: shipmentId };
		if (arApplications && arApplications.length > 0) {
			body.ar_applications = arApplications;
		}
		return this.http.post(this.setEndPoint('tpl3pl/shipments/approve_and_invoice'), body);
	}

	rejectShipment(shipmentId: number, reason: string): Observable<any> {
		return this.http.post(this.setEndPoint('tpl3pl/shipments/reject'), { shipment_id: shipmentId, reason });
	}

	holdShipment(shipmentId: number): Observable<any> {
		return this.http.post(this.setEndPoint('tpl3pl/shipments/hold'), { shipment_id: shipmentId });
	}

	releaseShipmentHold(shipmentId: number): Observable<any> {
		return this.http.post(this.setEndPoint('tpl3pl/shipments/release_hold'), { shipment_id: shipmentId });
	}

	/** Dialog data for resolving a SKU mismatch: shipped 3PL codes vs the order's open lines + suggestions. */
	getSkuMismatchDetail(shipmentId: number): Observable<{
		success: boolean;
		message?: string;
		shipment_id?: number;
		orderno?: string;
		shipped?: Array<{ sku: string; description: string; qty: number; in_stockmaster: boolean; existing_alias: string; matched: boolean }>;
		open_lines?: Array<{ stkcode: string; description: string; remaining: number }>;
		suggestions?: Record<string, string>;
		aliases?: Array<{ alias_sku: string; canonical_sku: string; note: string | null; active: number }>;
	}> {
		return this.http.post<any>(this.setEndPoint('tpl3pl/shipments/sku_mismatch_detail'), { shipment_id: shipmentId });
	}

	/** Save 3PL code -> canonical stockid aliases, then optionally re-invoice the shipment. */
	saveSkuAliases(
		mappings: Array<{ alias_sku: string; canonical_sku: string; note?: string }>,
		shipmentId?: number,
		reinvoice = true
	): Observable<{
		success: boolean;
		message?: string;
		saved?: Array<{ alias_sku: string; canonical_sku: string }>;
		errors?: string[];
		reinvoice?: { success: boolean; message?: string };
	}> {
		const body: Record<string, unknown> = { mappings, reinvoice };
		if (shipmentId && shipmentId > 0) {
			body.shipment_id = shipmentId;
		}
		return this.http.post<any>(this.setEndPoint('tpl3pl/shipments/save_sku_aliases'), body);
	}

	retryShipmentInvoice(
		shipmentId: number,
		arApplications?: Array<{ id: number; amount: number }>
	): Observable<any> {
		const body: Record<string, unknown> = { shipment_id: shipmentId };
		if (arApplications && arApplications.length > 0) {
			body.ar_applications = arApplications;
		}
		return this.http.post(this.setEndPoint('tpl3pl/shipments/retry_invoice'), body);
	}

	reauthShipmentInvoice(shipmentId: number): Observable<{
		success: boolean;
		message?: string;
		trans_id?: string;
		void_attempted?: number;
	}> {
		return this.http.post<any>(this.setEndPoint('tpl3pl/shipments/reauth_for_invoice'), { shipment_id: shipmentId });
	}

	/** Post sales invoice with no card capture or payment lines (operator override). */
	invoiceShipmentOnAccount(
		shipmentId: number,
		arApplications?: Array<{ id: number; amount: number }>
	): Observable<{ success: boolean; message?: string; invoiced_on_account?: boolean }> {
		const body: Record<string, unknown> = { shipment_id: shipmentId, confirm: true };
		if (arApplications && arApplications.length > 0) {
			body.ar_applications = arApplications;
		}
		return this.http.post<any>(this.setEndPoint('tpl3pl/shipments/invoice_on_account'), body);
	}

	markShipmentInvoiced(shipmentId: number, force = false): Observable<{ success: boolean; message?: string; needs_force?: boolean; already?: boolean }> {
		return this.http.post<any>(this.setEndPoint('tpl3pl/shipments/mark_invoiced'), { shipment_id: shipmentId, force });
	}

	/** Customer profile cards for the shipment’s order (3PL operator attach flow). */
	getShipmentCustomerCards(shipmentId: number): Observable<{
		success: boolean;
		message?: string;
		orderno?: number;
		debtorno?: string;
		branchcode?: string;
		cards?: Array<{
			card_id: string;
			label: string;
			card_type?: string;
			expiry_month?: string;
			expiry_year?: string;
		}>;
		current_order_card?: {
			card_id?: string;
			card_type?: string;
			card_number?: string;
			name?: string;
		} | null;
		credit_cards_active?: boolean;
	}> {
		const method = 'tpl3pl/shipments/customer_cards&shipment_id=' + encodeURIComponent(String(shipmentId));
		return this.http.get<{
			success: boolean;
			message?: string;
			orderno?: number;
			debtorno?: string;
			branchcode?: string;
			cards?: Array<{
				card_id: string;
				label: string;
				card_type?: string;
				expiry_month?: string;
				expiry_year?: string;
			}>;
			current_order_card?: {
				card_id?: string;
				card_type?: string;
				card_number?: string;
				name?: string;
			} | null;
			credit_cards_active?: boolean;
		}>(this.setEndPoint(method));
	}

	/** Copy a saved profile card onto salesorderpaymentmethod for the order. */
	attachSavedCardToShipmentOrder(shipmentId: number, cardId: string): Observable<{
		success: boolean;
		message?: string;
		orderno?: number;
		card_id?: string;
	}> {
		return this.http.post<{
			success: boolean;
			message?: string;
			orderno?: number;
			card_id?: string;
		}>(this.setEndPoint('tpl3pl/shipments/attach_saved_card_to_order'), {
			shipment_id: shipmentId,
			card_id: cardId
		});
	}

	/** Saved cards for a sales order (no tpl3pl_shipments row — e.g. 3PL export queue). */
	getExportQueueOrderCustomerCards(orderno: string): Observable<{
		success: boolean;
		message?: string;
		orderno?: number;
		debtorno?: string;
		branchcode?: string;
		cards?: Array<{
			card_id: string;
			label: string;
			card_type?: string;
			expiry_month?: string;
			expiry_year?: string;
		}>;
		current_order_card?: {
			card_id?: string;
			card_type?: string;
			card_number?: string;
			name?: string;
		} | null;
		credit_cards_active?: boolean;
	}> {
		const on = (orderno || '').trim();
		const method = 'tpl3pl/shipments/customer_cards&orderno=' + encodeURIComponent(on);
		return this.http.get(this.setEndPoint(method)) as Observable<{
			success: boolean;
			message?: string;
			orderno?: number;
			debtorno?: string;
			branchcode?: string;
			cards?: Array<{
				card_id: string;
				label: string;
				card_type?: string;
				expiry_month?: string;
				expiry_year?: string;
			}>;
			current_order_card?: {
				card_id?: string;
				card_type?: string;
				card_number?: string;
				name?: string;
			} | null;
			credit_cards_active?: boolean;
		}>;
	}

	attachSavedCardToOrderByOrderno(orderno: string, cardId: string): Observable<{
		success: boolean;
		message?: string;
		orderno?: number;
		card_id?: string;
	}> {
		return this.http.post<{
			success: boolean;
			message?: string;
			orderno?: number;
			card_id?: string;
		}>(this.setEndPoint('tpl3pl/shipments/attach_saved_card_to_order'), {
			orderno: (orderno || '').trim(),
			card_id: cardId
		});
	}

	bulkApproveShipments(shipmentIds: number[]): Observable<any> {
		return this.http.post(this.setEndPoint('tpl3pl/shipments/bulk_approve'), { shipment_ids: shipmentIds });
	}

	bulkInvoiceShipments(
		shipmentIds: number[],
		arApplications?: Array<{ id: number; amount: number }>
	): Observable<any> {
		const body: Record<string, unknown> = { shipment_ids: shipmentIds };
		if (arApplications && arApplications.length > 0) {
			body.ar_applications = arApplications;
		}
		return this.http.post(this.setEndPoint('tpl3pl/shipments/bulk_invoice'), body);
	}

	getInvoicePreview(shipmentId: number): Observable<{
		success: boolean;
		message?: string;
		preview?: {
			shipment: {
				id: number;
				orderno: string;
				tracking_number: string;
				carrier: string;
				ship_date: string;
				invoice_status: string;
				invoice_message: string;
			};
			order: {
				orderno: string;
				debtorno: string;
				branchcode: string;
				customer_name: string;
				payterms: string;
				comments: string;
			};
			lines: Array<{
				sku: string;
				description: string;
				ordered: number;
				already_invoiced: number;
				remaining: number;
				shipment_qty: number;
				will_invoice: number;
				unit_price: number;
				discount_pct: number;
				line_total: number;
				mismatch: boolean;
			}>;
			totals: {
				subtotal: number;
				tax: number;
				freight: number;
				total: number;
				tax_authorities: Array<{ taxauthid: string; description: string; taxrate: number; total: number }>;
			};
			preauth: {
				enabled: boolean;
				active_auth_total?: number;
				invoice_total?: number;
				new_auth_required?: boolean;
				mismatch?: boolean;
				message?: string;
				all_auths?: Array<{
					auth_id: string;
					transid: string;
					amount: number;
					status: string;
					status_label: string;
					is_active: boolean;
					transdate: string;
					card_id: string;
					tokenid: string;
				}>;
				payment_cart?: {
					amount: number;
					has_card_hold: boolean;
				} | null;
			};
			shipment_slice_total?: number;
			ar_portal?: {
				settings: {
					apply_customer_ar_on_invoice: boolean;
					ar_auto_apply_types: number[];
					allow_manual_open_payment_application: boolean;
					credit_branch_scope: string;
					credit_max_auto_apply_amount: number | null;
				} | null;
				sources: Array<{
					id: number;
					type: number;
					type_label: string;
					transno: number;
					trandate: string;
					reference: string;
					available: number;
				}>;
				applied: Array<{
					amount: number;
					source_id: number;
					type: number;
					type_label: string;
					transno: number;
					reference: string;
					trandate: string;
				}>;
				capture_reduction_cap: number;
			};
		};
	}> {
		return this.http.get<any>(this.setEndPoint('tpl3pl/shipments/invoice_preview&shipment_id=' + shipmentId));
	}

	/** Preview customer invoice + tracking email (recipients, subject, HTML) before/after send — 3PL shipments UI. */
	getCustomerInvoiceEmailPreview(shipmentId: number): Observable<{
		success: boolean;
		message?: string;
		preview?: {
			email_enabled_in_config: boolean;
			recipients: string[];
			recipient_source: string;
			recipient_source_label: string;
			from_email: string;
			sender_name: string;
			subject: string;
			html_body: string;
			text_body: string;
			invoice_ref_display: string;
			greeting: string;
			has_erp_invoice: boolean;
			erp_invoice_transno: string | null;
			customer_invoice_email_sent_at: string | null;
		};
	}> {
		return this.http.get<any>(this.setEndPoint('tpl3pl/shipments/customer_invoice_email_preview&shipment_id=' + shipmentId));
	}

	sendTestCustomerInvoiceEmail(shipmentId: number, to: string): Observable<{ success: boolean; message: string }> {
		return this.http.post<{ success: boolean; message: string }>(
			this.setEndPoint('tpl3pl/shipments/send_test_email'),
			{ shipment_id: shipmentId, to }
		);
	}

	getInventorySyncPreview(warehouse?: string): Observable<{ success: boolean; message?: string; request_payload?: any }> {
		const method = 'tpl3pl/inventory/sync_preview';
		const body: any = {};
		if (warehouse) { body.warehouse = warehouse; }
		return this.http.post<{ success: boolean; message?: string; request_payload?: any }>(this.setEndPoint(method), body);
	}

	/** Long timeout: PGS pulls many pages server-side; PHP/nginx limits are separate. */
	private static readonly inventorySyncHttpMs = 3600000;

	syncInventory(warehouse?: string, opts?: { new_skus_only?: boolean }): Observable<any> {
		const method = 'tpl3pl/inventory/sync';
		const body: any = {};
		if (warehouse) {body.warehouse = warehouse;}
		if (opts?.new_skus_only) {body.new_skus_only = true;}
		return this.http.post(this.setEndPoint(method), body).pipe(
			timeout(Tpl3plService.inventorySyncHttpMs)
		);
	}

	clearInventorySync(): Observable<{ success: boolean; message?: string; deleted?: number }> {
		return this.http.post<{ success: boolean; message?: string; deleted?: number }>(this.setEndPoint('tpl3pl/inventory/clear'), {});
	}

	getInventoryStatus(warehouse?: string, cacheBust?: boolean): Observable<any> {
		const method = 'tpl3pl/inventory/status';
		let params = new HttpParams();
		if (warehouse) {
			params = params.set('warehouse', warehouse);
		}
		if (cacheBust) {
			params = params.set('_t', Date.now().toString());
		}
		const url = this.setEndPoint(method);
		return this.http.get(url, { params });
	}

	getInventory(filters?: {
		warehouse?: string;
		stockid?: string;
		status?: string;
		search?: string;
		sort?: string;
		order?: 'asc' | 'desc';
		limit?: number;
		offset?: number;
		cacheBust?: boolean;
	}): Observable<{
		success: boolean;
		data: InventoryItem[];
		total: number;
		limit: number;
		offset: number;
	}> {
		const method = 'tpl3pl/inventory/list';
		let params = new HttpParams();
		if (filters) {
			if (filters.warehouse) { params = params.set('warehouse', filters.warehouse); }
			if (filters.stockid) { params = params.set('stockid', filters.stockid); }
			if (filters.status) { params = params.set('status', filters.status); }
			if (filters.search != null && filters.search !== '') { params = params.set('search', filters.search); }
			if (filters.sort) { params = params.set('sort', filters.sort); }
			if (filters.order) { params = params.set('order', filters.order); }
			if (filters.limit != null) { params = params.set('limit', String(filters.limit)); }
			if (filters.offset != null) { params = params.set('offset', String(filters.offset)); }
			if (filters.cacheBust) { params = params.set('_t', Date.now().toString()); }
		}
		return this.http.get<{
			success: boolean;
			data: InventoryItem[];
			total: number;
			limit: number;
			offset: number;
		}>(this.setEndPoint(method), { params });
	}

	getLogs(filters?: {
		log_type?: string;
		status?: string;
		orderno?: string;
		from_date?: string;
		to_date?: string;
		limit?: number;
		offset?: number;
	}): Observable<{
		success: boolean;
		data: LogEntry[];
		total: number;
		limit: number;
		offset: number;
	}> {
		let method = 'tpl3pl/logs';
		if (filters) {
			if (filters.log_type) {method += '&log_type=' + filters.log_type;}
			if (filters.status) {method += '&status=' + filters.status;}
			if (filters.orderno) {method += '&orderno=' + filters.orderno;}
			if (filters.from_date) {method += '&from_date=' + filters.from_date;}
			if (filters.to_date) {method += '&to_date=' + filters.to_date;}
			if (filters.limit) {method += '&limit=' + filters.limit;}
			if (filters.offset) {method += '&offset=' + filters.offset;}
		}
		return this.http.get<{
			success: boolean;
			data: LogEntry[];
			total: number;
			limit: number;
			offset: number;
		}>(this.setEndPoint(method));
	}

	getLogsPost(filters?: {
		log_type?: string;
		status?: string;
		orderno?: string;
		from_date?: string;
		to_date?: string;
		limit?: number;
		offset?: number;
	}): Observable<{
		success: boolean;
		data: LogEntry[];
		total: number;
		limit: number;
		offset: number;
	}> {
		const method = 'tpl3pl/logs';
		return this.http.post<{
			success: boolean;
			data: LogEntry[];
			total: number;
			limit: number;
			offset: number;
		}>(this.setEndPoint(method), filters || {});
	}

	getStatus(
		cacheBust?: boolean,
		orderInvoiceSeries?: { from: string; to: string } | null
	): Observable<any> {
		const url = this.setEndPoint('tpl3pl/status');
		let params = new HttpParams();
		if (cacheBust) {
			params = params.set('_t', String(Date.now()));
		}
		if (orderInvoiceSeries?.from && orderInvoiceSeries?.to) {
			params = params.set('oi_series_from', orderInvoiceSeries.from);
			params = params.set('oi_series_to', orderInvoiceSeries.to);
		}
		return this.http.get(url, { params });
	}

	listUncapturedAuths(): Observable<{ success: boolean; preauth_enabled: boolean; items: any[]; total: number }> {
		return this.http.get<any>(this.setEndPoint('payments/pendingauths/list_uncaptured'));
	}

	listAuthsByOrderList(payload: { order_list: string[]; date_from?: string; date_to?: string }): Observable<{ success: boolean; items: any[] }> {
		return this.http.post<any>(this.setEndPoint('payments/pendingauths/list_by_orders'), payload);
	}

	listBackorderAuths(payload: { orderstatusid: number; date_from?: string; date_to?: string }): Observable<{ success: boolean; items: any[]; total: number }> {
		return this.http.post<any>(this.setEndPoint('payments/pendingauths/list_backorder_auths'), payload);
	}

	validateAuthStatuses(auths: { auth_id: string; transid: string }[]): Observable<{ success: boolean; results: any[] }> {
		return this.http.post<any>(this.setEndPoint('payments/pendingauths/validate_auth_statuses'), { auths });
	}

	voidAuth(payload: { auth_id: string; transid: string }): Observable<{ success: boolean; message: string }> {
		return this.http.post<any>(this.setEndPoint('payments/pendingauths/void'), payload);
	}

	captureAuth(payload: { transid: string; auth_id: string; orderno: string; amount: number }): Observable<{ success: boolean; message: string; transid: string; orderno: string; amount: number }> {
		return this.http.post<any>(this.setEndPoint('payments/pendingauths/capture_auth'), payload);
	}

	markCaptured(payload: { transid?: string; auth_id?: string; orderno: string; amount: number; note?: string }): Observable<{ success: boolean; message: string; orderno: string; amount: number }> {
		return this.http.post<any>(this.setEndPoint('payments/pendingauths/mark_captured'), payload);
	}

	/** Gateway capture + AR receipt + allocate to posted sales invoice (uncaptured hold after invoice). */
	captureAuthApplyInvoice(payload: {
		transid?: string;
		auth_id?: string;
		orderno: string;
		amount?: number;
		invoice_transno?: number;
	}): Observable<{
		success: boolean;
		message?: string;
		transid?: string;
		orderno?: string;
		amount?: string;
		payment_id?: string | number | null;
		invoice_id?: string | number | null;
		invoice_transno?: number | null;
		already_receipt?: boolean;
		already_allocated?: boolean;
		gateway_capture?: boolean;
	}> {
		return this.http.post<any>(this.setEndPoint('payments/pendingauths/capture_auth_apply_invoice'), payload);
	}
}
